import React, { useState, useEffect } from 'react';
import ActionMenu from '../components/common/ActionMenu';
import { CheckCircle2, Shirt, Undo2 } from 'lucide-react';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import { toast } from 'react-toastify';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

const GownRequests = () => {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchRequests();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [requests, filter, dateRange, searchTerm]);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const response = await api.get('/gowns/requests');
      setRequests(response.data);
    } catch (error) {
      console.error('Error fetching gown requests:', error);
      toast.error('Failed to fetch gown requests');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...requests];

    // Apply status filter
    if (filter !== 'all') {
      filtered = filtered.filter(req => req.status === filter);
    }

    // Apply date range filter
    if (dateRange.start) {
      filtered = filtered.filter(req => new Date(req.request_date) >= new Date(dateRange.start));
    }
    if (dateRange.end) {
      filtered = filtered.filter(req => new Date(req.request_date) <= new Date(dateRange.end));
    }

    // Apply search filter
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(req => 
        req.graduate_name?.toLowerCase().includes(searchLower) ||
        req.admin_number?.toLowerCase().includes(searchLower)
      );
    }

    setFilteredRequests(filtered);
  };

  const handleApprove = async (id) => {
    setLoading(true);
    try {
      await api.put(`/gowns/requests/${id}/approve`);
      toast.success('Gown request approved');
      fetchRequests();
    } catch (error) {
      toast.error('Error approving request');
    } finally {
      setLoading(false);
    }
  };

  const handleIssue = async (id) => {
    setLoading(true);
    try {
      await api.put(`/gowns/requests/${id}/issue`);
      toast.success('Gown marked as issued');
      fetchRequests();
    } catch (error) {
      toast.error('Error issuing gown');
    } finally {
      setLoading(false);
    }
  };

  const handleReturn = async (id) => {
    setLoading(true);
    try {
      await api.put(`/gowns/requests/${id}/return`);
      toast.success('Gown marked as returned');
      fetchRequests();
    } catch (error) {
      toast.error('Error marking gown as returned');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch(status) {
      case 'pending':
        return { text: 'Pending', color: 'bg-yellow-100 text-yellow-800' };
      case 'approved':
        return { text: 'Approved', color: 'bg-blue-100 text-blue-800' };
      case 'issued':
        return { text: 'Issued', color: 'bg-purple-100 text-purple-800' };
      case 'returned':
        return { text: 'Returned', color: 'bg-green-100 text-green-800' };
      default:
        return { text: status, color: 'bg-gray-100 text-gray-800' };
    }
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  const printReport = () => {
    const printWindow = window.open('', '_blank');

    printWindow.document.write(`
      <html>
        <head>
          <title>Gown Requests Report</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            h1 { color: #1e3a8a; text-align: center; }
            .report-info { margin-bottom: 20px; text-align: center; color: #666; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th { background-color: #1e3a8a; color: white; padding: 10px; text-align: left; }
            td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            tr:nth-child(even) { background-color: #f2f2f2; }
            .footer { margin-top: 30px; text-align: center; font-size: 12px; color: #666; }
          </style>
        </head>
        <body>
          <h1>Katine TTI - Gown Requests Report</h1>
          <div class="report-info">
            <p>Generated on: ${new Date().toLocaleString()}</p>
            <p>Total Requests: ${filteredRequests.length}</p>
            ${searchTerm ? `<p>Search: ${searchTerm}</p>` : ''}
            ${filter !== 'all' ? `<p>Status: ${filter.toUpperCase()}</p>` : ''}
            ${dateRange.start ? `<p>From: ${dateRange.start}</p>` : ''}
            ${dateRange.end ? `<p>To: ${dateRange.end}</p>` : ''}
          </div>
           <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Graduate Name</th>
                <th>Admin Number</th>
                <th>Department</th>
                <th>Request Date</th>
                <th>Returned Date</th>
                <th>Status</th>
               </thead>
            <tbody>
              ${filteredRequests.map((req, index) => `
                <tr>
                  <td>${index + 1}</td>
                  <td>${req.graduate_name || '—'}</td>
                  <td>${req.admin_number || '—'}</td>
                  <td>${req.department_name || '—'}</td>
                  <td>${new Date(req.request_date).toLocaleDateString()}</td>
                  <td>${req.returned_date ? new Date(req.returned_date).toLocaleDateString() : '—'}</td>
                  <td>${getStatusBadge(req.status).text}</td>
                 </tr>
              `).join('')}
            </tbody>
           </table>
          <div class="footer">
            <p>This is a system-generated report from Katine TTI Graduation Management System</p>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const exportToExcel = () => {
    const worksheetData = filteredRequests.map((req, index) => ({
      '#': index + 1,
      'Graduate Name': req.graduate_name || '—',
      'Admin Number': req.admin_number || '—',
      'Department': req.department_name || '—',
      'Request Date': new Date(req.request_date).toLocaleDateString(),
      'Returned Date': req.returned_date ? new Date(req.returned_date).toLocaleDateString() : '—',
      'Status': getStatusBadge(req.status).text
    }));

    import('xlsx').then(XLSX => {
      const worksheet = XLSX.utils.json_to_sheet(worksheetData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Gown Requests');
      const fileName = `gown_requests_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(workbook, fileName);
      toast.success('Report exported successfully');
    }).catch(error => {
      console.error('Export error:', error);
      toast.error('Failed to export');
    });
  };

  const getReturnedCount = () => {
    return requests.filter(r => r.status === 'returned').length;
  };

  const getNotReturnedCount = () => {
    return requests.filter(r => r.status !== 'returned' && r.status !== 'pending').length;
  };

  const getPendingCount = () => {
    return requests.filter(r => r.status === 'pending').length;
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Gown Requests Management</h1>
        <div className="flex gap-3">
          <Button variant="outline" onClick={printReport} disabled={loading}>
            🖨️ Print Report
          </Button>
          <Button variant="outline" onClick={exportToExcel} disabled={loading}>
            📊 Export to Excel
          </Button>
          <Button variant="outline" onClick={fetchRequests} disabled={loading}>
            Refresh
          </Button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500">Total Requests</p>
          <p className="text-2xl font-bold text-primary">{requests.length}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500">Pending</p>
          <p className="text-2xl font-bold text-yellow-600">{getPendingCount()}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500">Returned</p>
          <p className="text-2xl font-bold text-green-600">{getReturnedCount()}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500">Not Returned</p>
          <p className="text-2xl font-bold text-red-600">{getNotReturnedCount()}</p>
        </div>
      </div>

      {/* Search Bar */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <Input
              label=""
              type="text"
              placeholder="🔍 Search by Graduate Name or Admission Number..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full"
              style={{ height: '42px' }}
            />
          </div>
          {searchTerm && (
            <Button
              variant="outline"
              onClick={clearSearch}
              className="whitespace-nowrap"
              style={{ height: '42px' }}
            >
              Clear Search
            </Button>
          )}
        </div>
        {searchTerm && (
          <p className="text-xs text-gray-500 mt-2">
            Found {filteredRequests.length} result(s) for "{searchTerm}"
          </p>
        )}
      </div>

      {/* Filter Tabs */}
      <div className="flex flex-wrap gap-2 mb-6 border-b">
        <button
          onClick={() => setFilter('all')}
          className={`px-4 py-2 font-medium ${filter === 'all' ? 'border-b-2 border-primary text-primary' : 'text-gray-500'}`}
        >
          All ({requests.filter(r => filter === 'all' || (searchTerm ? filteredRequests.filter(fr => fr.status === r.status).length : r.status === r.status)).length})
        </button>
        <button
          onClick={() => setFilter('pending')}
          className={`px-4 py-2 font-medium ${filter === 'pending' ? 'border-b-2 border-primary text-primary' : 'text-gray-500'}`}
        >
          Pending ({requests.filter(r => r.status === 'pending').length})
        </button>
        <button
          onClick={() => setFilter('approved')}
          className={`px-4 py-2 font-medium ${filter === 'approved' ? 'border-b-2 border-primary text-primary' : 'text-gray-500'}`}
        >
          Approved ({requests.filter(r => r.status === 'approved').length})
        </button>
        <button
          onClick={() => setFilter('issued')}
          className={`px-4 py-2 font-medium ${filter === 'issued' ? 'border-b-2 border-primary text-primary' : 'text-gray-500'}`}
        >
          Issued ({requests.filter(r => r.status === 'issued').length})
        </button>
        <button
          onClick={() => setFilter('returned')}
          className={`px-4 py-2 font-medium ${filter === 'returned' ? 'border-b-2 border-primary text-primary' : 'text-gray-500'}`}
        >
          Returned ({requests.filter(r => r.status === 'returned').length})
        </button>
      </div>

      {/* Date Range Filter */}
      <div className="mb-6 flex flex-wrap gap-4 items-end">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
          <input
            type="date"
            value={dateRange.start}
            onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
            className="input-field w-40"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
          <input
            type="date"
            value={dateRange.end}
            onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
            className="input-field w-40"
          />
        </div>
        <Button
          variant="outline"
          onClick={() => setDateRange({ start: '', end: '' })}
          className="h-10"
        >
          Clear Dates
        </Button>
      </div>

      {/* Gown Requests Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full bg-white rounded-lg overflow-hidden shadow">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">#</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Graduate Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Admin Number</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Department</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Request Date</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Returned Date</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredRequests.length === 0 ? (
              <tr>
                <td colSpan="8" className="px-6 py-4 text-center text-gray-500">
                  {searchTerm ? `No gown requests found for "${searchTerm}"` : 'No gown requests found.'}
                </td>
              </tr>
            ) : (
              filteredRequests.map((req, index) => (
                <tr key={req.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {index + 1}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{req.graduate_name || '—'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500 font-mono">{req.admin_number || '—'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">{req.department_name || '—'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">
                      {new Date(req.request_date).toLocaleDateString()}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">
                      {req.returned_date ? new Date(req.returned_date).toLocaleDateString() : '—'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadge(req.status).color}`}>
                      {getStatusBadge(req.status).text}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <ActionMenu
                      label={`Actions for ${req.graduate_name || 'gown request'}`}
                      items={[
                        {
                          label: 'Approve request',
                          icon: CheckCircle2,
                          onClick: () => handleApprove(req.id),
                          disabled: loading || req.status !== 'pending',
                        },
                        {
                          label: 'Issue gown',
                          icon: Shirt,
                          onClick: () => handleIssue(req.id),
                          disabled: loading || req.status !== 'approved',
                        },
                        {
                          label: 'Mark returned',
                          icon: Undo2,
                          onClick: () => handleReturn(req.id),
                          disabled: loading || req.status !== 'issued',
                        },
                      ]}
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default GownRequests;
